// Part of settingsPages, split by screen to keep files short; ../settingsPages.ts merges the parts.
export const en = {
  general: {
    currencies: {
      title: 'Currencies',
      description:
        'Home currency {{home}}. Rates are how many {{home}} one unit of the currency buys.',
      tabCurrencies: 'Currencies',
      tabRates: 'Exchange rates',
      tabRevaluations: 'Revaluations',
      todaysRates: "Today's rates: {{rates}}.",
      removed: '{{code}} removed.',
      gettingRates: 'Getting rates…',
      getTodaysRates: "Get today's rates",
      more: 'More',
      enterRate: 'Enter an exchange rate',
      importRates: 'Import exchange rates',
      revalueCurrency: 'Revalue currency',
      addCurrency: 'Add currency',
      loadError: 'Could not load currencies',
      offTitle: 'Multicurrency is off',
      offHint:
        'Switch it on in Company settings › Advanced › Currency to bill customers, pay vendors and keep bank accounts in other currencies.',
      companySettings: 'Company settings',
      removeConfirm: 'Remove {{code}} and its exchange rates?',
      emptyTitle: 'No other currencies yet',
      emptyHint: 'Add the currencies your customers, vendors and bank accounts use.',
      colCurrency: 'Currency',
      colExchangeRate: 'Exchange rate',
      colAsOf: 'As of',
      colAction: 'Action',
      colDate: 'Date',
      colRate: 'Rate',
      colRateIn: 'Rate ({{home}})',
      colSource: 'Source',
      colChanged: 'Changed',
      colGainIn: 'Unrealised gain or loss ({{home}})',
      colEntries: 'Entries',
      colStatus: 'Status',
      homeCurrencyTag: '(home currency)',
      noRateYet: 'No rate yet',
      editRate: 'Edit exchange rate',
      moreFor: 'More for {{code}}',
      revalue: 'Revalue',
      rateHistory: 'Rate history',
      inUseTitle: 'Used by customers, vendors, accounts or transactions',
      remove: 'Remove',
      rateDeleted: 'Exchange rate deleted. Transactions keep the rate they were saved with.',
      allCurrencies: 'All currencies',
      rateRule:
        'A transaction uses the latest rate on or before its date unless another rate is entered on it.',
      noRatesTitle: 'No exchange rates yet',
      noRatesHint: "Enter rates, import a CSV or get today's rates from the rate provider.",
      deleteRateConfirm: 'Delete the {{code}} rate of {{date}}?',
      delete: 'Delete',
      revaluationUndone: '{{code}} revaluation of {{date}} undone.',
      noRevaluationsTitle: 'No revaluations yet',
      noRevaluationsHint:
        "Revalue a currency at month end to show open foreign balances at that day's rate.",
      reversed: 'reversed',
      statusUndone: 'Undone',
      statusPosted: 'Posted',
      undo: 'Undo',
      undoTitle: 'Undo the {{code}} revaluation of {{date}}',
      undoDescription:
        'The revaluation entry and its next-day reversal are both reversed on their own dates. The revaluation stays in this list as undone.',
      reason: 'Reason',
      reasonHint: 'Recorded in the audit log.',
      undoing: 'Undoing…',
      undoRevaluation: 'Undo revaluation',
    },
    currencyDialogs: {
      added: "{{code}} added. Enter its exchange rate, or get today's rates.",
      addTitle: 'Add currency',
      addDescription: 'Customers, vendors and bank or credit card accounts can then use it.',
      currency: 'Currency',
      date: 'Date',
      rate: 'Rate',
      adding: 'Adding…',
      add: 'Add',
      rateSaved: '{{rate}} saved for {{date}}.',
      rateTitle: 'Exchange rate',
      rateDescription:
        'How many {{home}} one unit of the currency buys on the day. New transactions on or after the day use it unless another rate is entered on the form.',
      rateHint: '{{currency}} in {{home}}, up to 10 decimals',
      imported: '{{added}} rates added and {{updated}} changed.',
      importTitle: 'Import exchange rates',
      importDescription:
        'A CSV with one rate per row: date (dd/MM/yyyy), currency code, rate in home currency. A header row is skipped. Nothing is saved while any row has an error.',
      file: 'File',
      pasteRows: 'Or paste the rows',
      pastePlaceholder: 'Date,Currency,Rate\n01/08/2026,USD,3.6725',
      rowError: 'Row {{line}}: {{message}}',
      checkResult: '{{added}} new, {{updated}} changed, {{unchanged}} unchanged. Ready to import.',
      check: 'Check',
      importing: 'Importing…',
    },
    revalue: {
      postedGain:
        '{{code}} revalued at {{rate}} on {{date}}: unrealised gain of {{amount}}, reversed on {{reversal}}.',
      postedLoss:
        '{{code}} revalued at {{rate}} on {{date}}: unrealised loss of {{amount}}, reversed on {{reversal}}.',
      title: 'Revalue currency',
      description:
        'Restates what customers and vendors owe in the currency, and its bank and credit card balances, in {{home}} at the rate on the date. The gain or loss is unrealised: it posts on the date and reverses the next day.',
      currency: 'Currency',
      date: 'Revaluation date',
      exchangeRate: 'Exchange rate',
      rateEntered: 'Rate entered for this revaluation',
      rateFrom: 'Rate of {{date}} from Currencies',
      noRate: 'No rate on or before this date: enter one',
      enterRate: 'Enter the rate to see the balances.',
      nothingOpen: 'Nothing is open in {{code}} on {{date}}.',
      tableLabel: 'Balances to revalue',
      colAccount: 'Account',
      colName: 'Name',
      colBalance: 'Balance ({{code}})',
      colBooked: 'Booked ({{code}})',
      colRevalued: 'Revalued ({{code}})',
      colGain: 'Gain or loss',
      footerGain: 'Unrealised gain to Exchange gain or loss',
      footerLoss: 'Unrealised loss to Exchange gain or loss',
      posting: 'Posting…',
      revalue: 'Revalue',
    },
    currencySettings: {
      title: 'Currency',
      homeCurrency: 'Home currency',
      multicurrency: 'Multicurrency',
      currencies: 'Currencies',
      noneYet: 'None yet',
      manage: 'Manage currencies and rates',
      confirmError: 'Confirm that multicurrency cannot be switched off again.',
      turnedOn: 'Multicurrency is on. Add currencies on the Currencies page.',
      useLabel: 'Use multicurrency: customers, vendors and bank accounts in other currencies',
      homeStays:
        'The home currency stays {{home}}. Each customer, vendor, bank and credit card account keeps the currency it is created with once it has transactions.',
      understandLabel: 'I understand that multicurrency cannot be switched off again',
    },
    currencyLib: {
      source: { manual: 'Entered', import: 'Imported', provider: 'Rate provider' },
    },
    currency: {
      rateEntered: 'Rate entered for this transaction',
      rateFrom: 'Rate of {{date}} from Currencies',
      noRate: 'No {{code}} rate on or before this date: enter one',
      exchangeRate: 'Exchange rate',
      enterRate: 'Enter the {{code}} exchange rate',
    },
    users: {
      title: 'Manage users',
      description: "Who can open this client's books.",
      addUser: 'Add user',
      editUser: 'Edit user',
      loadError: 'Could not load users',
      clientUsers: 'Client users',
      accountingStaff: 'Accounting staff',
      invitedNote:
        'Invited users get access when they first sign in with this email address. Sign-in emails are coming soon.',
      noUsers: 'No users yet.',
      colName: 'Name',
      colEmail: 'Email',
      colRole: 'Role',
      colCloseRights: 'Close rights',
      colStatus: 'Status',
      colAdded: 'Added',
      colAction: 'Action',
      you: 'You',
      active: 'Active',
      invited: 'Invited',
      moreActionsFor: 'More actions for {{name}}',
      cannotRemoveSelf: 'You cannot remove your own access',
      removeUser: 'Remove user',
      role: {
        outss_admin: 'Admin',
        outss_accountant: 'Accountant',
        client_owner: 'Client owner',
        client_viewer: 'Client viewer',
      },
      roleHelp: {
        client_owner: 'Sees everything in the books; approvals and uploads are coming soon.',
        client_viewer: 'Read-only access to reports, sales and expenses.',
        outss_accountant: 'Accounting staff: keeps the books, posts and reverses entries.',
        outss_admin: 'Accounting staff with access to every client and to admin roles.',
      },
      updated: '{{name}} updated.',
      invitedToast: '{{email}} invited.',
      invalidEmail: 'Enter a valid email address.',
      inviteHint: 'Invite someone by email and choose what they can do.',
      nameHint: 'Optional; defaults to the email.',
      closeRightsLabel: 'Can lock and unlock periods (close rights)',
      sendInvite: 'Send invite',
      removedToast: '{{name}} no longer has access.',
      removeTitle: 'Remove {{name}}?',
      removeDescription:
        "They lose access to this client's books straight away. Everything they did stays in the audit log. You can add them again later.",
    },
    sentEmails: {
      title: 'Sent emails',
      description:
        'Invoices, credit notes, sales receipts and quotes emailed from Outss Accounting.',
      descriptionKept:
        'Invoices, credit notes, sales receipts and quotes emailed from Outss Accounting. This server keeps each email here and does not deliver it.',
      loadError: 'Could not load sent emails',
      emptyTitle: 'No emails yet',
      emptyHint: 'Use Send on an approved invoice, credit note, sales receipt or quote.',
      colDateTime: 'Date and time',
      colSubject: 'Subject',
      colAttachment: 'Attachment',
      from: 'From',
      to: 'To',
      cc: 'Cc',
      document: 'Document',
      status: 'Status',
      sentBy: 'sent by {{name}}',
      keptHere: '(kept here, not delivered)',
      download: 'Download',
    },
    attachments: {
      title: 'Attachments',
      description:
        'Files attached to invoices, bills, expenses, journal entries and payments. Uploads waiting to be recorded are in Documents.',
      loadError: 'Could not load attachments',
      emptyTitle: 'No attachments yet',
      emptyHint:
        'Attach files on a saved invoice, bill, expense or journal entry, or record an upload from Documents.',
      find: 'Find a file or transaction',
      colFile: 'File',
      colAttachedTo: 'Attached to',
      colUploadedBy: 'Uploaded by',
      colDate: 'Date',
    },
    ai: {
      title: 'AI Accountant',
      sendRow: 'Send document text to TypeSafe for classification',
      onConfigured: 'On: TypeSafe Jev ({{model}}) suggests vendors, accounts and tax codes',
      onNotConfigured: 'On, but TypeSafe is not set up on this server: rules are used',
      off: 'Off: rules suggest vendors, accounts and tax codes',
      whatIsSent: 'What is sent',
      whatIsSentValue:
        'Only the text read from each file and the names in the chart of accounts, vendor list and tax codes; never the file. A person approves every entry.',
      saved: 'AI Accountant settings saved.',
      checkbox: 'AI Accountant: send document text to TypeSafe for classification',
    },
    einvoicing: {
      title: 'E-invoicing',
      sendRow: 'Send invoices and credit notes as e-invoices',
      startDate: 'Start date',
      datedFrom: 'Invoices dated from {{date}}',
      fromNow: 'Everything approved from now on',
      legalRegistration: 'Legal registration',
      electronicAddress: 'Electronic address',
      needsTrn: 'Needs the company TRN',
      serviceProvider: 'Service provider',
      mockProvider: 'Test service provider (nothing leaves Outss Accounting)',
      stillNeeded: 'Still needed',
      saved: 'E-invoicing saved.',
      turnedOn: 'E-invoicing is on. Invoices and credit notes are sent when approved.',
      turnedOff: 'E-invoicing is off. Nothing more is sent.',
      checkbox: 'Send invoices and credit notes as e-invoices when they are approved',
      startDateHint: 'Invoices dated before this are marked as not required.',
      registrationType: 'Registration type',
      registrationNumber: 'Registration number',
      tradeLicenceNumber: 'Trade licence number',
      legalIdType: {
        TL: 'Trade licence',
        EID: 'Emirates ID',
        PAS: 'Passport',
        CD: 'Cabinet decision',
      },
    },
    gst: {
      title: 'GST registration',
      notRegistered: 'Not registered',
      state: 'State',
      registrationType: 'Registration type',
      effectiveDate: 'Effective date of registration',
      returnPeriod: 'GST return period',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      taxPack: 'Tax pack',
      indiaGst: 'India GST',
      saved: 'GST registration saved.',
      gstinHint: '15 characters, from the registration certificate.',
    },
    features: {
      title: 'Features',
      posting: {
        staff: 'Accounting staff only',
        approval: 'Client drafts, staff approve and post',
        direct: 'Client posts directly',
      },
      onWith: 'On · {{posting}}',
      saved: 'Features saved.',
      whoPosts: 'Who posts {{feature}}',
      requirements:
        'Sales needs Invoices; Inventory needs Invoices or Sales, and Purchases. A client owner who posts directly keeps that part of the books; accounting staff review afterwards.',
    },
  },
};

export const ar = {
  general: {
    currencies: {
      title: 'العملات',
      description:
        'العملة الأساسية {{home}}. السعر هو عدد وحدات {{home}} مقابل وحدة واحدة من العملة.',
      tabCurrencies: 'العملات',
      tabRates: 'أسعار الصرف',
      tabRevaluations: 'إعادة التقييم',
      todaysRates: 'أسعار اليوم: {{rates}}.',
      removed: 'تمت إزالة {{code}}.',
      gettingRates: 'جارٍ جلب الأسعار…',
      getTodaysRates: 'جلب أسعار اليوم',
      more: 'المزيد',
      enterRate: 'إدخال سعر صرف',
      importRates: 'استيراد أسعار الصرف',
      revalueCurrency: 'إعادة تقييم عملة',
      addCurrency: 'إضافة عملة',
      loadError: 'تعذّر تحميل العملات',
      offTitle: 'تعدد العملات غير مفعّل',
      offHint:
        'فعّله من إعدادات الشركة › متقدم › العملة لإصدار فواتير للعملاء والدفع للموردين والاحتفاظ بحسابات بنكية بعملات أخرى.',
      companySettings: 'إعدادات الشركة',
      removeConfirm: 'إزالة {{code}} وأسعار صرفها؟',
      emptyTitle: 'لا توجد عملات أخرى بعد',
      emptyHint: 'أضف العملات التي يستخدمها عملاؤك ومورّدوك وحساباتك البنكية.',
      colCurrency: 'العملة',
      colExchangeRate: 'سعر الصرف',
      colAsOf: 'بتاريخ',
      colAction: 'الإجراء',
      colDate: 'التاريخ',
      colRate: 'السعر',
      colRateIn: 'السعر ({{home}})',
      colSource: 'المصدر',
      colChanged: 'آخر تعديل',
      colGainIn: 'الربح أو الخسارة غير المحققة ({{home}})',
      colEntries: 'القيود',
      colStatus: 'الحالة',
      homeCurrencyTag: '(العملة الأساسية)',
      noRateYet: 'لا يوجد سعر بعد',
      editRate: 'تعديل سعر الصرف',
      moreFor: 'المزيد لـ {{code}}',
      revalue: 'إعادة تقييم',
      rateHistory: 'سجل الأسعار',
      inUseTitle: 'مستخدمة لدى عملاء أو موردين أو حسابات أو معاملات',
      remove: 'إزالة',
      rateDeleted: 'تم حذف سعر الصرف. تحتفظ المعاملات بالسعر الذي حُفظت به.',
      allCurrencies: 'جميع العملات',
      rateRule: 'تستخدم المعاملة آخر سعر في تاريخها أو قبله ما لم يُدخل عليها سعر آخر.',
      noRatesTitle: 'لا توجد أسعار صرف بعد',
      noRatesHint: 'أدخل الأسعار أو استورد ملف CSV أو اجلب أسعار اليوم من مزوّد الأسعار.',
      deleteRateConfirm: 'حذف سعر {{code}} بتاريخ {{date}}؟',
      delete: 'حذف',
      revaluationUndone: 'تم التراجع عن إعادة تقييم {{code}} بتاريخ {{date}}.',
      noRevaluationsTitle: 'لا توجد عمليات إعادة تقييم بعد',
      noRevaluationsHint:
        'أعد تقييم العملة في نهاية الشهر لإظهار الأرصدة الأجنبية المفتوحة بسعر ذلك اليوم.',
      reversed: 'عُكس في',
      statusUndone: 'تم التراجع',
      statusPosted: 'مرحّلة',
      undo: 'تراجع',
      undoTitle: 'التراجع عن إعادة تقييم {{code}} بتاريخ {{date}}',
      undoDescription:
        'يُعكس قيد إعادة التقييم وقيد عكسه في اليوم التالي، كلٌّ في تاريخه. وتبقى إعادة التقييم في هذه القائمة بحالة "تم التراجع".',
      reason: 'السبب',
      reasonHint: 'يُسجَّل في سجل التدقيق.',
      undoing: 'جارٍ التراجع…',
      undoRevaluation: 'التراجع عن إعادة التقييم',
    },
    currencyDialogs: {
      added: 'تمت إضافة {{code}}. أدخل سعر صرفها أو اجلب أسعار اليوم.',
      addTitle: 'إضافة عملة',
      addDescription:
        'يمكن بعدها للعملاء والموردين والحسابات البنكية وحسابات بطاقات الائتمان استخدامها.',
      currency: 'العملة',
      date: 'التاريخ',
      rate: 'السعر',
      adding: 'جارٍ الإضافة…',
      add: 'إضافة',
      rateSaved: 'تم حفظ السعر {{rate}} بتاريخ {{date}}.',
      rateTitle: 'سعر الصرف',
      rateDescription:
        'عدد وحدات {{home}} مقابل وحدة واحدة من العملة في ذلك اليوم. تستخدمه المعاملات الجديدة في ذلك اليوم أو بعده ما لم يُدخل سعر آخر في النموذج.',
      rateHint: '{{currency}} بعملة {{home}}، حتى 10 منازل عشرية',
      imported: 'الأسعار المضافة: {{added}}، والمعدّلة: {{updated}}.',
      importTitle: 'استيراد أسعار الصرف',
      importDescription:
        'ملف CSV بسعر واحد في كل صف: التاريخ (dd/MM/yyyy)، ورمز العملة، والسعر بالعملة الأساسية. يُتجاهل صف العناوين. لا يُحفظ شيء ما دام في أي صف خطأ.',
      file: 'الملف',
      pasteRows: 'أو الصق الصفوف',
      pastePlaceholder: 'Date,Currency,Rate\n01/08/2026,USD,3.6725',
      rowError: 'الصف {{line}}: {{message}}',
      checkResult:
        'جديدة: {{added}}، معدّلة: {{updated}}، دون تغيير: {{unchanged}}. جاهزة للاستيراد.',
      check: 'تحقق',
      importing: 'جارٍ الاستيراد…',
    },
    revalue: {
      postedGain:
        'أُعيد تقييم {{code}} بسعر {{rate}} بتاريخ {{date}}: ربح غير محقق بقيمة {{amount}}، يُعكس بتاريخ {{reversal}}.',
      postedLoss:
        'أُعيد تقييم {{code}} بسعر {{rate}} بتاريخ {{date}}: خسارة غير محققة بقيمة {{amount}}، تُعكس بتاريخ {{reversal}}.',
      title: 'إعادة تقييم عملة',
      description:
        'يعيد احتساب المبالغ المستحقة على العملاء وللموردين بهذه العملة، وأرصدة حساباتها البنكية وبطاقات الائتمان، بعملة {{home}} وفق السعر في التاريخ المحدد. الربح أو الخسارة غير محققين: يُرحَّلان في التاريخ المحدد ويُعكسان في اليوم التالي.',
      currency: 'العملة',
      date: 'تاريخ إعادة التقييم',
      exchangeRate: 'سعر الصرف',
      rateEntered: 'سعر مُدخل لإعادة التقييم هذه',
      rateFrom: 'سعر {{date}} من صفحة العملات',
      noRate: 'لا يوجد سعر في هذا التاريخ أو قبله: أدخل سعراً',
      enterRate: 'أدخل السعر لعرض الأرصدة.',
      nothingOpen: 'لا توجد أرصدة مفتوحة بعملة {{code}} بتاريخ {{date}}.',
      tableLabel: 'الأرصدة المطلوب إعادة تقييمها',
      colAccount: 'الحساب',
      colName: 'الاسم',
      colBalance: 'الرصيد ({{code}})',
      colBooked: 'المسجّل ({{code}})',
      colRevalued: 'بعد إعادة التقييم ({{code}})',
      colGain: 'الربح أو الخسارة',
      footerGain: 'ربح غير محقق إلى حساب أرباح أو خسائر الصرف',
      footerLoss: 'خسارة غير محققة إلى حساب أرباح أو خسائر الصرف',
      posting: 'جارٍ الترحيل…',
      revalue: 'إعادة تقييم',
    },
    currencySettings: {
      title: 'العملة',
      homeCurrency: 'العملة الأساسية',
      multicurrency: 'تعدد العملات',
      currencies: 'العملات',
      noneYet: 'لا يوجد بعد',
      manage: 'إدارة العملات والأسعار',
      confirmError: 'أكّد أنه لا يمكن إيقاف تعدد العملات بعد تفعيله.',
      turnedOn: 'تم تفعيل تعدد العملات. أضف العملات من صفحة العملات.',
      useLabel: 'استخدام تعدد العملات: عملاء وموردون وحسابات بنكية بعملات أخرى',
      homeStays:
        'تبقى العملة الأساسية {{home}}. ويحتفظ كل عميل ومورد وحساب بنكي وحساب بطاقة ائتمان بالعملة التي أُنشئ بها بمجرد أن تكون له معاملات.',
      understandLabel: 'أدرك أنه لا يمكن إيقاف تعدد العملات بعد تفعيله',
    },
    currencyLib: {
      source: { manual: 'مُدخل يدوياً', import: 'مستورد', provider: 'مزوّد الأسعار' },
    },
    currency: {
      rateEntered: 'سعر مُدخل لهذه المعاملة',
      rateFrom: 'سعر {{date}} من صفحة العملات',
      noRate: 'لا يوجد سعر لـ {{code}} في هذا التاريخ أو قبله: أدخل سعراً',
      exchangeRate: 'سعر الصرف',
      enterRate: 'أدخل سعر صرف {{code}}',
    },
    users: {
      title: 'إدارة المستخدمين',
      description: 'من يمكنه فتح دفاتر هذا العميل.',
      addUser: 'إضافة مستخدم',
      editUser: 'تعديل المستخدم',
      loadError: 'تعذّر تحميل المستخدمين',
      clientUsers: 'مستخدمو العميل',
      accountingStaff: 'فريق المحاسبة',
      invitedNote:
        'يحصل المستخدمون المدعوون على الوصول عند أول تسجيل دخول بعنوان البريد الإلكتروني هذا. رسائل تسجيل الدخول قريباً.',
      noUsers: 'لا يوجد مستخدمون بعد.',
      colName: 'الاسم',
      colEmail: 'البريد الإلكتروني',
      colRole: 'الدور',
      colCloseRights: 'صلاحية الإقفال',
      colStatus: 'الحالة',
      colAdded: 'تاريخ الإضافة',
      colAction: 'الإجراء',
      you: 'أنت',
      active: 'نشط',
      invited: 'مدعو',
      moreActionsFor: 'إجراءات أخرى لـ {{name}}',
      cannotRemoveSelf: 'لا يمكنك إزالة صلاحية وصولك',
      removeUser: 'إزالة المستخدم',
      role: {
        outss_admin: 'مسؤول',
        outss_accountant: 'محاسب',
        client_owner: 'مالك (العميل)',
        client_viewer: 'مُطّلع (العميل)',
      },
      roleHelp: {
        client_owner: 'يرى كل ما في الدفاتر؛ الاعتمادات والتحميلات قريباً.',
        client_viewer: 'اطلاع فقط على التقارير والمبيعات والمصروفات.',
        outss_accountant: 'فريق المحاسبة: يمسك الدفاتر ويرحّل القيود ويعكسها.',
        outss_admin: 'فريق المحاسبة مع صلاحية الوصول إلى جميع العملاء وأدوار الإدارة.',
      },
      updated: 'تم تحديث {{name}}.',
      invitedToast: 'تمت دعوة {{email}}.',
      invalidEmail: 'أدخل عنوان بريد إلكتروني صحيحاً.',
      inviteHint: 'ادعُ شخصاً عبر البريد الإلكتروني واختر ما يمكنه فعله.',
      nameHint: 'اختياري؛ يُستخدم البريد الإلكتروني افتراضياً.',
      closeRightsLabel: 'يمكنه قفل الفترات وفتحها (صلاحية الإقفال)',
      sendInvite: 'إرسال الدعوة',
      removedToast: 'لم يعد لـ {{name}} حق الوصول.',
      removeTitle: 'إزالة {{name}}؟',
      removeDescription:
        'يفقد الوصول إلى دفاتر هذا العميل فوراً. ويبقى كل ما قام به في سجل التدقيق. ويمكنك إضافته مرة أخرى لاحقاً.',
    },
    sentEmails: {
      title: 'الرسائل المرسلة',
      description:
        'الفواتير والإشعارات الدائنة وإيصالات المبيعات وعروض الأسعار المرسلة بالبريد الإلكتروني من Outss Accounting.',
      descriptionKept:
        'الفواتير والإشعارات الدائنة وإيصالات المبيعات وعروض الأسعار المرسلة بالبريد الإلكتروني من Outss Accounting. يحتفظ هذا الخادم بكل رسالة هنا ولا يسلّمها.',
      loadError: 'تعذّر تحميل الرسائل المرسلة',
      emptyTitle: 'لا توجد رسائل بعد',
      emptyHint: 'استخدم "إرسال" على فاتورة أو إشعار دائن أو إيصال مبيعات أو عرض سعر معتمد.',
      colDateTime: 'التاريخ والوقت',
      colSubject: 'الموضوع',
      colAttachment: 'المرفق',
      from: 'من',
      to: 'إلى',
      cc: 'نسخة',
      document: 'المستند',
      status: 'الحالة',
      sentBy: 'أرسلها {{name}}',
      keptHere: '(محفوظة هنا، لم تُسلَّم)',
      download: 'تنزيل',
    },
    attachments: {
      title: 'المرفقات',
      description:
        'الملفات المرفقة بالفواتير وفواتير الموردين والمصروفات وقيود اليومية والدفعات. التحميلات التي تنتظر التسجيل موجودة في المستندات.',
      loadError: 'تعذّر تحميل المرفقات',
      emptyTitle: 'لا توجد مرفقات بعد',
      emptyHint:
        'أرفق الملفات بفاتورة أو فاتورة مورد أو مصروف أو قيد يومية محفوظ، أو سجّل تحميلاً من المستندات.',
      find: 'ابحث عن ملف أو معاملة',
      colFile: 'الملف',
      colAttachedTo: 'مرفق بـ',
      colUploadedBy: 'حمّله',
      colDate: 'التاريخ',
    },
    ai: {
      title: 'المحاسب الذكي',
      sendRow: 'إرسال نص المستندات إلى TypeSafe لتصنيفها',
      onConfigured: 'مفعّل: يقترح TypeSafe Jev ({{model}}) الموردين والحسابات ورموز الضريبة',
      onNotConfigured: 'مفعّل، لكن TypeSafe غير مُعدّ على هذا الخادم: تُستخدم القواعد',
      off: 'متوقف: تقترح القواعد الموردين والحسابات ورموز الضريبة',
      whatIsSent: 'ما الذي يُرسل',
      whatIsSentValue:
        'النص المقروء من كل ملف وأسماء دليل الحسابات وقائمة الموردين ورموز الضريبة فقط؛ ولا يُرسل الملف أبداً. ويعتمد شخصٌ كل قيد.',
      saved: 'تم حفظ إعدادات المحاسب الذكي.',
      checkbox: 'المحاسب الذكي: إرسال نص المستندات إلى TypeSafe لتصنيفها',
    },
    einvoicing: {
      title: 'الفوترة الإلكترونية',
      sendRow: 'إرسال الفواتير والإشعارات الدائنة كفواتير إلكترونية',
      startDate: 'تاريخ البدء',
      datedFrom: 'الفواتير المؤرخة من {{date}}',
      fromNow: 'كل ما يُعتمد من الآن فصاعداً',
      legalRegistration: 'التسجيل القانوني',
      electronicAddress: 'العنوان الإلكتروني',
      needsTrn: 'يتطلب رقم التسجيل الضريبي للشركة',
      serviceProvider: 'مزوّد الخدمة',
      mockProvider: 'مزوّد خدمة تجريبي (لا يغادر شيء Outss Accounting)',
      stillNeeded: 'ما زال مطلوباً',
      saved: 'تم حفظ إعدادات الفوترة الإلكترونية.',
      turnedOn: 'تم تفعيل الفوترة الإلكترونية. تُرسل الفواتير والإشعارات الدائنة عند اعتمادها.',
      turnedOff: 'تم إيقاف الفوترة الإلكترونية. لن يُرسل شيء بعد الآن.',
      checkbox: 'إرسال الفواتير والإشعارات الدائنة كفواتير إلكترونية عند اعتمادها',
      startDateHint: 'تُعلَّم الفواتير المؤرخة قبل هذا التاريخ بأنها غير مطلوبة.',
      registrationType: 'نوع التسجيل',
      registrationNumber: 'رقم التسجيل',
      tradeLicenceNumber: 'رقم الرخصة التجارية',
      legalIdType: {
        TL: 'رخصة تجارية',
        EID: 'الهوية الإماراتية',
        PAS: 'جواز السفر',
        CD: 'قرار مجلس الوزراء',
      },
    },
    gst: {
      title: 'تسجيل GST',
      notRegistered: 'غير مسجّل',
      state: 'الولاية',
      registrationType: 'نوع التسجيل',
      effectiveDate: 'تاريخ سريان التسجيل',
      returnPeriod: 'فترة إقرار GST',
      monthly: 'شهري',
      quarterly: 'ربع سنوي',
      taxPack: 'الحزمة الضريبية',
      indiaGst: 'GST الهند',
      saved: 'تم حفظ تسجيل GST.',
      gstinHint: '15 حرفاً، من شهادة التسجيل.',
    },
    features: {
      title: 'الميزات',
      posting: {
        staff: 'فريق المحاسبة فقط',
        approval: 'العميل يُعدّ المسودات، والفريق يعتمد ويرحّل',
        direct: 'العميل يرحّل مباشرة',
      },
      onWith: 'مفعّل · {{posting}}',
      saved: 'تم حفظ الميزات.',
      whoPosts: 'من يرحّل {{feature}}',
      requirements:
        'تتطلب المبيعات ميزة الفواتير؛ ويتطلب المخزون الفواتير أو المبيعات، والمشتريات. مالك العميل الذي يرحّل مباشرة يتولى ذلك الجزء من الدفاتر؛ ويراجعه فريق المحاسبة لاحقاً.',
    },
  },
};
